import React, { useState, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';

import Button from 'components/Button/Button';
import Filter from 'components/Filter/Filter';
import Pagination from 'components/Pagination/Pagination';
import DirectionIcon from 'components/DirectionIcon/DirectionIcon';
import { pushNotificationWithTimeout } from 'modules/notifications/notificationsSlice';

import colors from 'theme/Colors.module.scss';
import './Params.scss';

import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  TableContainer,
  Progress,
  Flex,
} from '@chakra-ui/react';

import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { useGetCompanyParamsQuery } from 'modules/params/companyParamsApi';

export default function Params() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [selectedRowId, setSelectedRowId] = useState(null);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  });

  // Raw search input (not debounced)
  const [searchInput, setSearchInput] = useState('');

  // Debounced search parameters
  const [defaultSearchParameters, setDefaultSearchParameters] = useState({
    search: '',
    sort: [],
  });

  const debouncedSearch = useMemo(
    () =>
      debounce(query => {
        setDefaultSearchParameters(prev => ({ ...prev, search: query }));
        setPagination(prev => ({ ...prev, pageIndex: 0 })); // Reset to first page
      }, 500), // Adjust debounce delay
    [] // No dependencies, so it's created only once
  );

  // Wrap it in useCallback so it doesn't change on every render
  const handleDebouncedSearch = useCallback(
    query => {
      debouncedSearch(query);
    },
    [debouncedSearch]
  );

  const {
    data: companyParamsData,
    error,
    isLoading,
    isFetching,
  } = useGetCompanyParamsQuery({
    ...defaultSearchParameters,
    page: pagination.pageIndex + 1,
    size: pagination.pageSize,
    sort: defaultSearchParameters.sort || [],
  });

  const columns = [
    {
      accessorKey: 'key',
      header: 'Key',
      enableSorting: false,
      cell: props => <Text>{props.getValue()}</Text>,
    },
    {
      accessorKey: 'label',
      header: 'Label',
      enableSorting: false,
      cell: props => <Text>{props.getValue()}</Text>,
    },
    {
      accessorKey: 'scopes',
      header: 'Scopes',
      enableSorting: false,
      cell: props => {
        return (
          <Text>
            {props.getValue().length > 1 ? 'All' : props.getValue()[0]}
          </Text>
        );
      },
    },
  ];

  const table = useReactTable({
    data: companyParamsData?.data.entities || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true, // Enable manual pagination
    state: {
      pagination,
    },
  });

  if (error) {
    dispatch(
      pushNotificationWithTimeout({
        type: 'error',
        message: 'Failed to fetch params data',
      })
    );
  }

  // Handle rows per clicked
  const handleRowClick = row => {
    setSelectedRowId(prev => (prev === row.id ? null : row.id)); // Toggle selection
    navigate(`/companyParams/${row.original.id}`);
  };

  return (
    <>
      <Box className="params-header">
        <Box className="params-header__left">
          <Button
            type="button"
            url="/companyParams/create"
            icon="add"
            text="New Param"
            textColor={colors.slate}
            btnStyle="transparent"
          />
        </Box>
        <Box className="params-header__nav"></Box>
        <Box className="params-header__right"></Box>
      </Box>

      <Box className="params-body">
        <Box height="60px" className="rail rail-rule">
          <Filter
            placeholder="Search Params"
            value={searchInput}
            onChange={event => {
              setSearchInput(event); // Update raw input immediately
              handleDebouncedSearch(event); // Trigger debounced update
            }}
          />
        </Box>

        <Box className="params-table" borderWidth="1px">
          <TableContainer>
            {(isLoading || isFetching) && (
              <Progress size="xs" isIndeterminate />
            )}
            <Table size="md" variant="simple">
              <Thead zIndex={1} bg="white">
                {table.getHeaderGroups().map(headerGroup => (
                  <Tr className="params-table__row" key={headerGroup.id}>
                    {headerGroup.headers.map(header => {
                      const textAlign =
                        header.column.columnDef.textAlign || 'left';
                      return (
                        <Th
                          className="params-table__header"
                          key={header.id}
                          whiteSpace="nowrap"
                          textAlign={textAlign}
                        >
                          {header.column.getCanSort() ? (
                            <Flex justify={textAlign} align="center">
                              <Text
                                className="params-table__header-text"
                                mr={2}
                                cursor={
                                  header.column.getCanSort()
                                    ? 'pointer'
                                    : 'default'
                                }
                              >
                                {header.isPlaceholder
                                  ? null
                                  : flexRender(
                                      header.column.columnDef.header,
                                      header.getContext()
                                    )}
                              </Text>

                              <Box
                                cursor={
                                  header.column.getCanSort()
                                    ? 'pointer'
                                    : 'default'
                                }
                              >
                                {defaultSearchParameters.sort[0]?.field ===
                                header.id ? (
                                  <DirectionIcon
                                    type={'alpha'}
                                    direction={
                                      defaultSearchParameters.sort[0]
                                        ?.direction || 'asc'
                                    }
                                    style={{
                                      color:
                                        defaultSearchParameters.sort[0]
                                          ?.field === header.id
                                          ? colors.primary900
                                          : colors.neutral300,
                                    }}
                                  />
                                ) : (
                                  <DirectionIcon
                                    type={'alpha'}
                                    direction={'normal'}
                                    style={{
                                      color:
                                        defaultSearchParameters.sort[0]
                                          ?.field === header.id
                                          ? colors.primary900
                                          : colors.neutral300,
                                    }}
                                  />
                                )}
                              </Box>
                            </Flex>
                          ) : (
                            <Text className="params-table__header-text" mr={2}>
                              {header.isPlaceholder
                                ? null
                                : flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                  )}
                            </Text>
                          )}
                        </Th>
                      );
                    })}
                  </Tr>
                ))}
              </Thead>
              <Tbody>
                {table.getRowModel().rows.map(row => {
                  return (
                    <Tr
                      key={row.id}
                      onClick={() => handleRowClick(row)}
                      bg={row.id === selectedRowId ? 'gray.100' : 'transparent'}
                      color={colors.slate}
                      _hover={{
                        bg: colors.lighterGrey,
                        textColor: colors.deepSlate2,
                      }}
                      cursor="pointer"
                    >
                      {row.getVisibleCells().map((cell, index) => {
                        const textAlign =
                          cell.column.columnDef.textAlign || 'left';

                        return (
                          <Td
                            className="params-table__body-cell"
                            px={index === 0 ? '0px' : '15px'}
                            py="0px"
                            key={cell.id}
                            textAlign={textAlign}
                          >
                            {index === 0 ? (
                              <Box className="params-table__first-cell-container">
                                <Box
                                  className="params-table__indicator"
                                  bg={`${
                                    row.original.inactive_at
                                      ? 'gray.400'
                                      : 'green.400'
                                  }`}
                                ></Box>
                                <Box className="params-table__first-cell">
                                  {flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext()
                                  )}
                                </Box>
                              </Box>
                            ) : (
                              flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )
                            )}
                          </Td>
                        );
                      })}
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
        <Pagination
          table={table}
          data={companyParamsData}
          pagination={pagination}
          setPagination={setPagination}
          setSelectedRowId={setSelectedRowId}
        />
      </Box>
    </>
  );
}
